import React from 'react';
import "@/App.css";
import './lib/i18n';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import AuthCallback from './pages/AuthCallback';
import Login from './pages/auth/Login';
import CustomerRegister from './pages/auth/CustomerRegister';
import InterpreterRegister from './pages/auth/InterpreterRegister';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// Admin
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminInterpreters from './pages/admin/AdminInterpreters';
import AdminJobs from './pages/admin/AdminJobs';
import AdminJobDetail from './pages/admin/AdminJobDetail';
import AdminApprovals from './pages/admin/AdminApprovals';
import AdminReports from './pages/admin/AdminReports';
import AdminUsers from './pages/admin/AdminUsers';

// Customer
import CustomerDashboard from './pages/customer/CustomerDashboard';
import CustomerBooking from './pages/customer/CustomerBooking';
import CustomerJobs from './pages/customer/CustomerJobs';
import CustomerPaymentSuccess from './pages/customer/CustomerPaymentSuccess';

// Interpreter
import InterpreterDashboard from './pages/interpreter/InterpreterDashboard';
import InterpreterProfile from './pages/interpreter/InterpreterProfile';
import InterpreterJobs from './pages/interpreter/InterpreterJobs';
import InterpreterAvailability from './pages/interpreter/InterpreterAvailability';

const RoleHome = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin' || user.role === 'pm') return <Navigate to="/admin" replace />;
  if (user.role === 'customer') return <Navigate to="/customer" replace />;
  if (user.role === 'interpreter') return <Navigate to="/interpreter" replace />;
  return <Navigate to="/login" replace />;
};

function AppRouter() {
  const location = useLocation();
  // Synchronous check: session_id in URL fragment must be processed before other routing
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register/customer" element={<CustomerRegister />} />
      <Route path="/register/interpreter" element={<InterpreterRegister />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin', 'pm']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/customers" element={<ProtectedRoute allowedRoles={['admin', 'pm']}><AdminCustomers /></ProtectedRoute>} />
      <Route path="/admin/interpreters" element={<ProtectedRoute allowedRoles={['admin', 'pm']}><AdminInterpreters /></ProtectedRoute>} />
      <Route path="/admin/jobs" element={<ProtectedRoute allowedRoles={['admin', 'pm']}><AdminJobs /></ProtectedRoute>} />
      <Route path="/admin/jobs/:id" element={<ProtectedRoute allowedRoles={['admin', 'pm']}><AdminJobDetail /></ProtectedRoute>} />
      <Route path="/admin/approvals" element={<ProtectedRoute allowedRoles={['admin', 'pm']}><AdminApprovals /></ProtectedRoute>} />
      <Route path="/admin/reports" element={<ProtectedRoute allowedRoles={['admin', 'pm']}><AdminReports /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['admin']}><AdminUsers /></ProtectedRoute>} />

      <Route path="/customer" element={<ProtectedRoute allowedRoles={['customer']}><CustomerDashboard /></ProtectedRoute>} />
      <Route path="/customer/book" element={<ProtectedRoute allowedRoles={['customer']}><CustomerBooking /></ProtectedRoute>} />
      <Route path="/customer/jobs" element={<ProtectedRoute allowedRoles={['customer']}><CustomerJobs /></ProtectedRoute>} />
      <Route path="/customer/payment/success" element={<ProtectedRoute allowedRoles={['customer']}><CustomerPaymentSuccess /></ProtectedRoute>} />

      <Route path="/interpreter" element={<ProtectedRoute allowedRoles={['interpreter']}><InterpreterDashboard /></ProtectedRoute>} />
      <Route path="/interpreter/profile" element={<ProtectedRoute allowedRoles={['interpreter']}><InterpreterProfile /></ProtectedRoute>} />
      <Route path="/interpreter/jobs" element={<ProtectedRoute allowedRoles={['interpreter']}><InterpreterJobs /></ProtectedRoute>} />
      <Route path="/interpreter/availability" element={<ProtectedRoute allowedRoles={['interpreter']}><InterpreterAvailability /></ProtectedRoute>} />

      <Route path="/" element={<RoleHome />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
